'use client';

import NextImage from 'next/image';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import type { CmsButtonTarget } from '@/lib/cms-button-target';
import type { SustainabilitySocialNeevProgram } from '@/types/sustainability';

const FALLBACK_NEEV_URL = 'https://theneev.org/';
const FALLBACK_LOGO = '/images/neev-logo.png';

const FALLBACK_PROGRAMS: SustainabilitySocialNeevProgram[] = [
  {
    id: 'logo-1',
    imageUrl: '/images/company_logo1.png',
    title: 'Hunar',
    subtitle: 'Skills & livelihoods',
  },
  {
    id: 'logo-2',
    imageUrl: '/images/company_logo2.png',
    title: 'Sanrachna',
    subtitle: 'Rural Infrastructure',
  },
  {
    id: 'logo-3',
    imageUrl: '/images/company_logo3.png',
    title: 'Udaan',
    subtitle: 'Education & Youth',
  },
  {
    id: 'logo-4',
    imageUrl: '/images/company_logo4.png',
    title: 'Arogyam',
    subtitle: 'Healthcare & Wellness',
  },
  {
    id: 'logo-5',
    imageUrl: '/images/company_logo5.png',
    title: 'Eco Green',
    subtitle: 'Environment & Conservation',
  },
];

const FALLBACK_SUBHEADING =
  'An initiative driven by Vivek Patni, Neev works in the communities around our plants. If we take from the earth to make cement, we give back to the people who live on it. Neev operates across five pillars for long-term community development.';

interface SustainabilitySocialNeevSectionProps {
  cmsHeadingFull?: string | null;
  cmsSubheadingHtml?: string | null;
  cmsPrograms?: SustainabilitySocialNeevProgram[];
  cmsLogoUrl?: string | null;
  cmsLogoAlt?: string | null;
  cmsEyebrowHeading?: string | null;
  cmsButtonText?: string | null;
  cmsButtonUrl?: string | null;
  cmsButtonTarget?: CmsButtonTarget | null;
}

function ProgramCard({
  program,
  align = 'center',
}: {
  program: SustainabilitySocialNeevProgram;
  align?: 'left' | 'center';
}) {
  const isCentered = align === 'center';

  return (
    <article className="flex h-full min-h-[132px] flex-col rounded-2xl bg-white px-4 py-4 shadow-[0_1px_4px_rgba(0,0,0,0.05)] sm:min-h-[140px] sm:px-5 sm:py-5 md:min-h-[148px] md:px-6 md:py-6">
      <div className="h-0.5 w-10 shrink-0 rounded-full bg-[#ed1c22]" aria-hidden />
      <div
        className={`mt-4 flex flex-1 flex-col sm:mt-5 ${
          isCentered ? 'items-center text-center' : 'items-start text-left'
        }`}
      >
        {program.imageUrl ? (
          <div className="relative h-11 w-16 sm:h-12 sm:w-[72px] md:h-14 md:w-20">
            <NextImage
              src={program.imageUrl}
              alt={program.title}
              fill
              className={`object-contain ${isCentered ? '' : 'object-left'}`}
              sizes="80px"
            />
          </div>
        ) : (
          <div className="h-11 sm:h-12 md:h-14" />
        )}
        <h3 className="mt-3 text-sm font-bold leading-tight text-[#1a1a1a] sm:mt-4 sm:text-base">
          {program.title}
        </h3>
        <p className="mt-1 text-[11px] leading-5 text-stone-500 sm:text-xs">{program.subtitle}</p>
      </div>
    </article>
  );
}

function ProgramsGrid({ programs }: { programs: SustainabilitySocialNeevProgram[] }) {
  const topPrograms = programs.slice(0, 3);
  const bottomPrograms = programs.slice(3, 5);

  return (
    <>
      {/* Mobile — single column */}
      <div className="flex flex-col gap-3 sm:hidden">
        {programs.map((program) => (
          <ProgramCard key={program.id} program={program} align="left" />
        ))}
      </div>

      {/* Tablet — 2-column grid (3 + 2 rows) */}
      <div className="hidden grid-cols-2 gap-3 sm:grid md:hidden">
        {topPrograms.map((program, index) => (
          <div key={program.id} className={index === 2 ? 'col-span-2' : undefined}>
            <ProgramCard program={program} align="center" />
          </div>
        ))}
        {bottomPrograms.map((program) => (
          <ProgramCard key={program.id} program={program} align="center" />
        ))}
      </div>

      {/* Tablet landscape / small desktop — 6-col staggered grid */}
      <div className="hidden grid-cols-6 gap-3 md:grid lg:gap-3">
        {topPrograms.map((program) => (
          <div key={program.id} className="col-span-2">
            <ProgramCard program={program} align="center" />
          </div>
        ))}
        {bottomPrograms.map((program) => (
          <div key={program.id} className="col-span-3">
            <ProgramCard program={program} align="center" />
          </div>
        ))}
      </div>
    </>
  );
}

export default function SustainabilitySocialNeevSection({
  cmsHeadingFull,
  cmsSubheadingHtml,
  cmsPrograms,
  cmsLogoUrl,
  cmsLogoAlt,
  cmsEyebrowHeading,
  cmsButtonText,
  cmsButtonUrl,
  cmsButtonTarget,
}: SustainabilitySocialNeevSectionProps) {
  const programs = cmsPrograms?.length ? cmsPrograms : FALLBACK_PROGRAMS;
  const logoSrc = cmsLogoUrl?.trim() || FALLBACK_LOGO;
  const logoAlt = cmsLogoAlt?.trim() || 'NEEV — A Vivek Patni Initiative';
  const eyebrow = cmsEyebrowHeading?.trim() || 'Our Programs';
  const buttonText = cmsButtonText?.trim() || 'Explore NEEV Initiative';
  const buttonUrl = cmsButtonUrl?.trim() || FALLBACK_NEEV_URL;
  const isExternal = /^https?:/i.test(buttonUrl);

  const headingText = cmsHeadingFull?.trim()
    ? cmsHeadingFull.replace(/^\s*Neev\s*[:\-—]\s*/i, '')
    : null;

  return (
    <section className="bg-[#f4f1ed] max-sm:-mt-[1px] max-sm:-mb-[1px]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-start gap-8 sm:gap-10 md:gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:gap-14 xl:gap-16">
          {/* Left — branding & copy */}
          <div className="w-full min-w-0 lg:max-w-[520px]">
            <div className="mb-6 max-w-[180px] sm:mb-8 sm:max-w-[200px] md:max-w-[220px]">
              <NextImage
                src={logoSrc}
                alt={logoAlt}
                width={520}
                height={260}
                className="h-auto w-full mix-blend-multiply"
              />
            </div>

            <h2 className="text-[1.75rem] font-bold leading-[1.12] tracking-tight text-[#1a1a1a] sm:text-4xl md:text-[2.75rem] lg:text-5xl">
              {headingText ?? (
                <>
                  Strong Foundations
                  <br />
                  aren&apos;t Just for Buildings
                </>
              )}
            </h2>

            {cmsSubheadingHtml ? (
              <div
                dangerouslySetInnerHTML={{ __html: cmsSubheadingHtml }}
                className="mt-4 text-sm leading-7 text-stone-600 sm:mt-5 sm:text-[15px] sm:leading-8 md:mt-6 [&_p]:mb-0 [&_p+p]:mt-4"
              />
            ) : (
              <p className="mt-4 text-sm leading-7 text-stone-600 sm:mt-5 sm:text-[15px] sm:leading-8 md:mt-6">
                {FALLBACK_SUBHEADING}
              </p>
            )}

            <div className="mt-6 h-px w-10 bg-[#ed1c22] sm:mt-8 md:mt-10" aria-hidden />

            <SwapTextButton
              text={buttonText}
              hoverText={buttonText}
              href={buttonUrl}
              {...getCmsButtonLinkProps(
                { target: cmsButtonTarget },
                isExternal ? '_blank' : undefined,
              )}
              variant="outline"
              className="!mt-6 !min-h-[46px] !w-full !justify-center !rounded-full !border-stone-800 !bg-[#f4f1ed] !px-6 !py-3 !text-sm !font-medium !text-stone-900 !shadow-none hover:!border-stone-900 hover:!bg-stone-900 hover:!text-white sm:!mt-8 sm:!w-auto sm:!justify-start sm:!px-7 md:!mt-10 sm:!text-[15px]"
            />
          </div>

          {/* Right — program cards */}
          <div className="w-full min-w-0">
            <p className="mb-4 text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-stone-400 sm:mb-5 md:mb-6">
              {eyebrow}
            </p>
            <ProgramsGrid programs={programs} />
          </div>
        </div>
      </div>
    </section>
  );
}
